// NOTA: La tabla de resultados de la validación de campos del
// archivo de importación maneja un stateKey "import-filters-table"
// que se guarda en la session y se borra desde el archivo
// ProtectedRoutes.tsx, una vez que el usuario sale de la página

import { useContext, useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  BoxFull,
  Button,
  Card,
  TransactionalSubheader,
  Dropdown,
} from "iuvity-ui-components";
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import { FileUpload } from 'primereact/fileupload';
import { AppContext } from "../../context/index";
import { DescriptionBox } from "../../components/pageComponents";
import { FilterMatchMode } from "primereact/api";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { Column } from "iuvity-ui-components/dist/column";
import { Tag } from "iuvity-ui-components/dist/tag";
import { MultiSelect } from "primereact/multiselect";
import { TableHeader } from "../../components/datatable/TableHeader";
import { useTranslations } from "../../hooks/useTranslations";
import { ConsoleTranslationsImportSchema } from "../../validations/schemas";
import Papa from "papaparse";
import { CSVLink } from "react-csv";
import { registerAudit } from "../../helpers/registerAudit";

interface ImportPageProps {
  headers?: string[];
}

const csvHeaders = [
  "id",
  "widgets",
  "key",
  "version",
  "es",
  "en",
  "type",
  "description",
  "createdat",
  "updatedat"
];

const RenderTable = ({ columns, data }: any) => {
  const {
    t,
    appState: { language },
  } = useContext(AppContext);

  const defaultFilterValues = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
    type: { value: null, matchMode: FilterMatchMode.IN },
    functionality: { value: null, matchMode: FilterMatchMode.IN },
    version: { value: null, matchMode: FilterMatchMode.IN },
  };
  const [selectedColumns, setSelectedColumns] = useState(columns);
  const [filters, setFilters] = useState(defaultFilterValues);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [statusDrodownValue, setStatusDrodownValue] = useState<string>('');
  const [valuesOfColumnFilterFields, setValuesOfColumnFilterFields] = useState<{
    functionality: string[];
    type: string[];
    version: string[];
  }>({
    functionality: [],
    type: [],
    version: [],
  });

  const onClearFiltersColumn = () => {
    setFilters(defaultFilterValues);
    setStatusDrodownValue('');
    setValuesOfColumnFilterFields({
      functionality: [],
      type: [],
      version: [],
    });
  };

  useEffect(() => {
    if (language)
      setSelectedColumns((prev: any) =>
        prev.map((col: any) => ({
          field: col.field,
          header: t(
            `Translations.${
              col.field === "version" ? "releaseversion" : col?.field
            }Label`
          ),
        }))
      );
  }, [t, language, setSelectedColumns]);

  const [disableClearFilterButton, setDisableClearFilterButton] = useState<{
    status: boolean;
    type: boolean;
    functionality: boolean;
    version: boolean;
  }>({
    status: false,
    type: false,
    functionality: false,
    version: false,
  });
  const [disableApplyFilterButton, setDisableApplyFilterButton] = useState<{
    status: boolean;
    type: boolean;
    functionality: boolean;
    version: boolean;
  }>({
    status: false,
    type: false,
    functionality: false,
    version: false,
  });

  const onClearFiltersPerTable = ( options: any, filtersFromSession: any ) => {
    setFilters({
      global: { value: options?.field === 'global' ? null : filtersFromSession?.global?.value, matchMode: FilterMatchMode.CONTAINS },
      status: { value: options?.field === 'status' ? null : filtersFromSession?.status?.value, matchMode: FilterMatchMode.EQUALS },
      type: { value: options?.field === 'type' ? null : filtersFromSession?.type?.value, matchMode: FilterMatchMode.IN },
      functionality: { value: options?.field === 'functionality' ? null : filtersFromSession?.functionality?.value, matchMode: FilterMatchMode.IN },
      version: { value: options?.field === 'version' ? null : filtersFromSession?.version?.value, matchMode: FilterMatchMode.IN },
    });

    // luego de dar click en limpiar, elimina los criterios de búsqueda.
    setDisableClearFilterButton((prev:any)=>({
      ...prev,
      [options?.field]: true,
    }));
    options?.field === 'status'
      ? setStatusDrodownValue('')
      : setValuesOfColumnFilterFields((prev)=>({
          ...prev,
          [options.field]: ['functionality','type','version'].includes(options.field) ? [] : '',
        }));
    setDisableApplyFilterButton((prev)=>({
      ...prev,
      [options?.field]: false
    }));
  };

  const statusBodyTemplate = (rowData: any) => (
    <Tag
      value={t(`Translations.${rowData.status}StatusLabel`)}
      severity={
        (['VALID'].includes(rowData.status) && 'success') ||
        (['INVALID'].includes(rowData.status) && 'danger') ||
        'warning'
      }
    />
  );

  const ItemsTemplate = (option: any) => {
    return (
      <div className="p-multiselect-representative-option">
        <span className="image-text">{option?.label}</span>
      </div>
    );
  };

  const multiselectFilterTemplate = (options: any, arrayWithoutDuplicates: any) => {
    return (<>
      <label htmlFor={options.field} className="block lb mt-2 mb-1">
        {t(`Translations.${options.field}Label`)}
      </label>
      <MultiSelect
        // @ts-ignore
        value={valuesOfColumnFilterFields[options.field]}
        display="chip"
        options={arrayWithoutDuplicates}
        itemTemplate={ItemsTemplate}
        placeholder={t(`Translations.${options.field}PlaceholderLabel`)} 
        className="mb-2"
        emptyFilterMessage={t("Translations.noAvailableMessage")}
        filter
        onChange={(e)=>{
          setValuesOfColumnFilterFields((prev)=>({
            ...prev,
            [options.field]: e.value,
          }));
          options.filterCallback(e.value);
          setDisableApplyFilterButton((prev)=>({
            ...prev,
            [options.field]: e?.value?.length > 0
          }));
        }}
      />
    </>)
  };

  const filterTemplate = (options: any) => {
    const arrayOriginal = data
      ?.map((label: any) =>
        ['type'].includes(options.field)
          ? ({
            value: t(`Translations.${label?.type.toLowerCase()}OptionLabel`),
            label: t(`Translations.${label?.type.toLowerCase()}OptionLabel`),
          })
          : ({
            label: label[`${options.field}`]?.split(', ')[0],
            value: label[`${options.field}`]?.split(', ')[0]
          }))
      ?.filter((item: { label: string, value: string }) => item.label)
      ?.sort((a: any, b: any) => a.label.localeCompare(b.label));

    const arrayWithoutDuplicates = arrayOriginal?.filter((object: any, currentIndex: any) => {
      return arrayOriginal.findIndex((duplicateObject: any) =>
        duplicateObject.label === object.label) === currentIndex;
    });

    return (
      options.field === 'status'
      ? statusFilterTemplate(options)
      : multiselectFilterTemplate(options, arrayWithoutDuplicates)
    )
  };

  const statuses:any = [
    `${t("Translations.VALIDStatusLabel")}`,
    `${t("Translations.INVALIDStatusLabel")}`,
  ];

  const statusItemTemplate = (option: any) => {
    return <span>{option}</span>;
  };

  const statusFilterTemplate = (options: any) => {
    return (
            <div>
              <label htmlFor={options.field} className="block lb mt-2 mb-3">
                {t(`Translations.${options.field}Label`)}
              </label>
              <Dropdown
                label=""
                value={statusDrodownValue}
                options={statuses}
                itemTemplate={statusItemTemplate}
                placeholder={t(`Translations.${options.field}FilterPlaceholder`)}
                className="p-column-filter mb-2"
                showClear={false}
                onChange={(e)=>{
                  setStatusDrodownValue(e.value);
                  options.filterCallback(t("Translations.INVALIDStatusLabel") === e.value ? 'INVALID' : 'VALID');
                  setDisableApplyFilterButton((prev)=>({
                    ...prev,
                    status: true
                  }));
                }}
              />
            </div>
    )
  };

  const filterClearTemplate = (options: any) => {
    const filtersFromSession = JSON.parse(sessionStorage?.getItem('import-filters-table') ?? '{}')?.filters;

    return (
      <Button
        label={t("Tenants.clearFilterLabel")}
        variant="p-button-text"
        disabled={(filtersFromSession?.hasOwnProperty('global')
            ? !filtersFromSession[options?.field]?.value : true)
            // @ts-ignore
            || disableClearFilterButton[options?.field]}
        onClick={() => onClearFiltersPerTable(options, filtersFromSession)}
      />
    )
  }

  const filterApplyTemplate = (options: any) => {
    return (
      <Button
        itemID={options?.field}
        label={t("Tenants.applyFilterLabel")}
        className="p-button"
        // @ts-ignore
        disabled={!disableApplyFilterButton[options.field]}
        onClick={() => {
          options.filterApplyCallback();
          setDisableClearFilterButton((prev:any)=>({
            ...prev,
            [options?.field]: false,
          }));
        }}
      />
    )
  };

  useEffect(() => {
    if (sessionStorage.getItem('import-filters-table')) {
      const dataSession = JSON.parse(sessionStorage.getItem('import-filters-table') || "");
      const global = dataSession.filters.global;
      setGlobalFilterValue(global);
    }
  }, [filters]);

  return (
    <DataTable
      className="mt-4"
      header={
        <TableHeader
          title={t("Translations.translationsTitle")}
          columns={columns}
          selectedColumns={selectedColumns}
          toggleColumns={setSelectedColumns}
          filters={filters}
          setFilters={setFilters}
          reload={()=>{}}
          onClearFiltersColumn={onClearFiltersColumn}
          globalFilterValue={globalFilterValue}
        />
      }
      currentPageReportTemplate={t(
        "ConsoleTranslations.rowsPaginatorTemplateLabel"
      )}
      paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
      emptyMessage={t("TableActions.emptyMessage")}
      rows={10}
      size="normal"
      editMode="row"
      dataKey="id"
      sortField="status"
      sortMode="single"
      sortOrder={1}
      filters={filters}
      value={data
        ?.map((label:any)=>({
          ...label,
          type: t(`Translations.${label.type.toLowerCase()}OptionLabel`),
        }))
      }
      filterDisplay="menu"
      stateKey="import-filters-table"
    >
      {selectedColumns.map((col: any) => (
        <Column
          key={col.field}
          field={col.field}
          header={col.header}
          sortable={true}
          align="left"
          filter={["status","functionality","type","version"].includes(col.field)}
          filterElement={(options) => filterTemplate(options)}
          filterApply={filterApplyTemplate}
          filterClear={filterClearTemplate}
          showFilterMenu
          showApplyButton
          showClearButton
          body={(rowData) => col.field === 'status' ? statusBodyTemplate(rowData) : rowData[col.field]}
        />
      ))}
    </DataTable>
  );
};

export const ImportPage: React.FC<ImportPageProps> = ({
  headers = csvHeaders,
}) => {

  const navigate = useNavigate();
  const {
    t,
    appState: { selectedApp },
  } = useContext(AppContext);

  const onAction = ()=>{};
  const onReturn = () => navigate("/translations");

  const onErrorExport = ()=>{
    document.getElementById('error-labels-download-link')?.click();
    registerAudit({
      role: selectedApp?.rolename,
      group: "",
      tenant: selectedApp?.tenantname,
      environment: selectedApp?.env,
      segment: selectedApp?.segment,
      functionality: "LABELS_FRONT",
      activity: "ERRORS_DOWNLOAD_LABELS_FRONT",
      object: "LABEL",
      initialValue: null,
      finalValue: `${errorLabelsData.length}`,
      status: "SUCCESS",
      errorCode: "",
      errorDescription: "",
      errorCause: "",
    });
  };

  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const handleModalProps = (actionType: "action" | "cancel" | "errorexport" | "import") => {
    switch (actionType) {
      case "action":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t(`Translations.saveModalTitle`),
          content: t(`Translations.saveModalContent`),
          onConfirm: onAction,
        }));
      case "cancel":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t("Translations.cancelModalTitle"),
          content: t("Translations.cancelModalContent"),
          onConfirm: onReturn,
        }));
      case "errorexport":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t("Translations.errorExportModalTitle"),
          content: t("Translations.errorExportModalContent"),
          onConfirm: onErrorExport,
        }));
      case "import":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t(`Translations.importLabelsModalTitle`),
          content: `${t(`Translations.importLabelsModalContent`)}`,
          onConfirm: onImport,
        }));
      default:
        return;
    }
  };

  // IMPORTER-TRANSLATIONS-LOGIC
  // ***************************
  const _columns = useMemo(() => {
    return [
      {
        field: "functionality",
        header: t("Translations.functionalityLabel"),
      },
      {
        field: "type",
        header: t("Translations.typeLabel"),
      },
      {
        field: "key",
        header: t("Translations.keyLabel"),
      },
      {
        field: "esvalue",
        header: t("Translations.esvalueLabel"),
      },
      {
        field: "envalue",
        header: t("Translations.envalueLabel"),
      },
      {
        field: "releaseversion",
        header: t("Translations.releaseversionLabel"),
      },
      {
        field: "createdat",
        header: t("Translations.createdatLabel"),
      },
      {
        field: "updatedat",
        header: t("Translations.updatedatLabel"),
      },
      {
        field: "actions",
        header: t("Translations.actionsLabel"),
      },
    ];
  }, [t]);

  const columns = [
    {
      field: "id",
      header: "id",
    },
    ..._columns
      .filter((col: any) => col?.field !== "actions")
      .map((col) => ({
        ...col,
        field: col?.field === "releaseversion" ? "version" : col?.field,
      })),
    {
      field: "description",
      header: t("Translations.descriptionLabel"),
    },
  ];

  const { translationAction, reloadFn } = useTranslations("DATA");
  const [parsedLabelsData, setParsedLabelsData] = useState<any[]>([]);
  const [uploadErrorMsg, setuploadErrorMsg] = useState<boolean>(false);
  const [disableUploadFile, setDisableUploadFile] = useState<boolean>(false);
  const [showResultFileValidation, setShowResultFileValidation] = useState<boolean>(false);
  const [filePath, setFilePath] = useState<string>("");
  const [errorLabelsData, setErrorLabelsData] = useState<any[]>([]);

  const onImport = async () => {
    const dataSuccess = parsedLabelsData.filter((item) => {
      const allValuesFilter = JSON.parse(sessionStorage?.getItem('import-filters-table') ?? '{}')?.filters;
      return (
          (!!allValuesFilter?.status?.value ? (item?.status === allValuesFilter?.status?.value) : true) &&
          (allValuesFilter?.type?.value?.length > 0 ? (allValuesFilter?.type?.value
            ?.find((component:string)=>t(`Translations.${item?.type.toLowerCase()}OptionLabel`) === component)) : true) &&
          (allValuesFilter?.functionality?.value?.length > 0 ? (allValuesFilter?.functionality?.value
            ?.find((widget:string)=>item?.functionality === widget)) : true) &&
          (allValuesFilter?.version?.value?.length > 0 ? (allValuesFilter?.version?.value
            ?.find((release:string)=>item?.version === release)) : true)
        )
    });

    const actionStatus = await translationAction("IMPORT", {
      applicationid: selectedApp?.appid,
      importInfo: dataSuccess.map((label) => ({
        id: label?.id,
        key: label?.key,
        description: label?.description,
        type: label?.type,
        widgets: label?.widgets?.split(', '),
        en: label?.envalue,
        es: label?.esvalue,
        version: label?.version,
      })),
    });
    if (actionStatus === "SUCCESS") {
      onReturn();
      reloadFn();
    }
  };

  const fieldsValidator = (label: any) => {
    const validationRules = ConsoleTranslationsImportSchema(t);
    const validations: any[] = [];

    ["functionality", "key", "type", "esvalue", "envalue", "description", "createdat", "updatedat"].forEach((key: any) => {
      const minLengthValue = ["createdat", "updatedat"].includes(key) ? 0 : (validationRules as any)[key].minLength.value;
      const maxLengthValue = ["createdat", "updatedat"].includes(key) ? 2048 : (validationRules as any)[key].maxLength.value;
      const patternValue = ["createdat", "updatedat"].includes(key) ? undefined : (validationRules as any)[key === 'key' ? 'key_import' : key].pattern.value;

      if (label[key]) {
        if (label[key].length < (key === "description" ? 0 : minLengthValue)) {
          validations.push(
            `${t("Translations.errorMessageStarter")} (${t(
              `Translations.${key}Label`
            )}) ${t("Translations.minLengthErrorMessage")} (${minLengthValue})`
          );
        }
        if (label[key].length > maxLengthValue) {
          validations.push(
            `${t("Translations.errorMessageStarter")} (${t(
              `Translations.${key}Label`
            )}) ${t("Translations.maxLengthErrorMessage")} (${maxLengthValue})`
          );
        }
        if ((key === 'functionality' && label[key].split(',')?.length === 1) || !["functionality","createdat", "updatedat"].includes(key)) {
          if (label[key].length > 0 && !label[key].match(patternValue)) {
            validations.push(
              `${t("Translations.errorMessageStarter")} (${t(
                `Translations.${key}Label`
              )}) ${t("Translations.patternErrorMessage")}`
            );
          }
        }
        if (["createdat", "updatedat"].includes(key) && label[key].length > 0 && isNaN(Date.parse(label[key]))) {
          validations.push(
            `${t("Translations.errorMessageStarter")} (${t(
              `Translations.${key}Label`
            )}) ${t("Translations.patternErrorMessage")}`
          );
        }
      }
    });
    return validations.join(". ");
  };

  const invoiceUploadHandler = ({ files }: any) => {
    const [file] = files;
    setFilePath(file.name);
    sessionStorage.setItem('ERRORS::FILEPATH', file.name);
    try {
      Papa.parse(file, {
        header: true,
        download: true,
        delimiter: ",",
        complete: (result: any) => {
          if (
            result?.meta?.fields.every((el: string) => headers.includes(el))
          ) {
            const parsedData = result?.data
              .filter((label: any) => label?.id)
              .map((label: any) => ({
                ...label,
                esvalue: label?.es,
                envalue: label?.en,
                functionality: label?.widgets || label?.functionality,
              }))
              .map((label: any) => ({
                ...label,
                validations: fieldsValidator(label),
                status: fieldsValidator(label) === '' ? "VALID" : "INVALID",
              }));
            setParsedLabelsData(parsedData);
            setErrorLabelsData(parsedData?.filter((label: any) => label?.validations.length > 0));
          } else {
            setuploadErrorMsg(true);
          }
        },
      });
      return true;
    } catch (error) {
      setuploadErrorMsg(true);
    }
  };

  const invoiceRemoveHandler = () => {
    setParsedLabelsData([]);
    setFilePath("");
    setuploadErrorMsg(false);
  };

  return (
    <>
      <TransactionalSubheader
        subheaderTitle={
          <div style={{ position: "relative", left: "-40px" }}>
            <i
              className="iu iu-icon-left pr-3"
              onClick={onReturn}
              aria-hidden="true"
              style={{
                fontSize: "var(--font-size-tablet-title)",
                cursor: "pointer",
              }}
            />
            {t("Translations.importTitle")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={t("Translations.translationsTitle")}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t("Menu.SetupLabel"),
              disabled: true
            },
            {
              label: t("Translations.translationsTitleFront"),
              command: onReturn,
              style: { fontFamily: "var(--fontBarlowSemiBold)" },
            },
            {
              label: t("Translations.importBreadcrumb"),
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
      </TransactionalSubheader>

      <>
        <DescriptionBox
          label={`Translations.importLabelDescription`}
        />
        <BoxFull
          className="flex flex-column align-items-center justify-content-center mb-4"
          style={{ background: "transparent" }}
        >
          <>
            <div
              className={`upload-container w-full ${
                Object.keys(parsedLabelsData).length > 0 ? "mb-4" : ""
              }`}
            >
              <div
                className="card mt-4"
                style={{
                  width: '100%',
                }}
              >
                <FileUpload
                  accept=".csv"
                  // maxFileSize={1000000}
                  cancelOptions={{
                    style: {
                      display: 'none',
                    }
                  }}
                  uploadOptions={{
                    style: {
                      display: 'none',
                    }
                  }}
                  chooseLabel={t("Translations.uploadFileComponentTitle")}
                  chooseOptions={{
                    icon: 'iu iu-icon-upload',
                    className: disableUploadFile ? ' p-disabled p-button-link' : 'p-button-link',
                  }}
                  onSelect={(e)=>{ 
                    invoiceUploadHandler(e)
                    setDisableUploadFile(true);
                  }}
                  onRemove={()=>{
                    sessionStorage.removeItem('import-filters-table');
                    setDisableUploadFile(false);
                    invoiceRemoveHandler();
                    setShowResultFileValidation(false);
                  }}
                />
              </div>
            </div>

            {uploadErrorMsg && (
              <span className="p-error block mt-5">
                {t("Translations.uploadFileErrorMsgLabel")}
              </span>
            )}

            {showResultFileValidation && parsedLabelsData.length > 0 && (
              <>
                <div className="flex justify-content-left w-full mt-2">
                    <p
                        className="boxTitle"
                        style={{width: '100%'}}>
                        {t("Translations.labelsToImportTitle")}
                    </p>
                </div>
                <span
                  dangerouslySetInnerHTML={
                    { __html: 
                      errorLabelsData?.length > 0
                        ? t("Translations.labelsToImportErrorDescription")
                        : t("Translations.labelsToImportDescription")
                    }}
                  className="block mt-2 mb-4 w-full"
                />
                <Card
                  style={{
                    width: '100%',
                  }}
                >
                  <RenderTable
                    columns={(()=>{
                      const columnstoShow = columns?.filter((col: any) =>
                        !["actions","createdat","updatedat","description"].includes(col?.field));

                      return errorLabelsData?.length > 0
                        ? [{
                          field: "status",
                          header: t("Translations.statusLabel")},
                          {
                            field: "validations",
                            header: t("Translations.validationsLabel")}, ...columnstoShow]
                        : [{
                          field: "status",
                          header: t("Translations.statusLabel")}
                          , ...columnstoShow]
                    })()}
                    data={parsedLabelsData}
                  />
                </Card>
              </>
            )}
          </>

          <div className="form-btngroup-footer my-5 p-0 gap-6">
            <Button
              variant="p-button-link"
              label={t(`Translations.cancelBtnLabel`)}
              onClick={onReturn}
            />
            {
              showResultFileValidation &&
              <Button
                variant="p-button-text"
                label={t(`Translations.errorsExportBtnLabel`)}
                onClick={() => handleModalProps("errorexport")}
                disabled={errorLabelsData?.length === 0}
              />
            }
            <Button
              variant="p-button"
              label={showResultFileValidation ? t("Translations.importBtnLabel") : t("Translations.uploadBtnLabel")}
              disabled={!disableUploadFile || (showResultFileValidation && errorLabelsData?.length > 0) || uploadErrorMsg}
              onClick={() => {
                !showResultFileValidation
                  ? setShowResultFileValidation(true)
                  : handleModalProps("import");
              }}
            />
          </div>
        </BoxFull>
      </>

      <CustomDialog
        title={modalProps.title}
        visible={modalProps.visible}
        content={modalProps.content}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />

      <CSVLink
        id="error-labels-download-link"
        headers={headers.map((key) => ({
          label: key,
          key,
        }))}
        data={errorLabelsData}
        enclosingCharacter={`"`}
        filename={`${t("Translations.errorsLabel")}-${filePath}`}
        style={{
          display: 'none',
        }}
      >
        {t("Translations.errorsReportBtnLabel")}
      </CSVLink>
    </>
  );
};
